// utils/createSafeThunk.js

import { createAsyncThunk } from "@reduxjs/toolkit";
import { buildStandardError } from "./buildStandardError";

// validationFn = (args) => string | null  => return error message or null
// apiCallFn = async (args) => response
export const createSafeThunk = ({
  typePrefix,
  source,
  validationFn,
  apiCallFn,
  onError,
}) => {
  return createAsyncThunk(
    typePrefix,
    async (args, { rejectWithValue, dispatch }) => {
      try {
        const validationError = validationFn?.(args);
        if (validationError) {
          const error = buildStandardError({
            source,
            type: "validation",
            message: validationError,
          });
          onError?.(error, dispatch);
          return rejectWithValue(error);
        }

        const response = await apiCallFn(args);

        //  If result is a Response, parse it normally
        if (response instanceof Response) {
          if (!response.ok) {
            const errorText = await response.text();
            const error = buildStandardError({
              source,
              type: "api",
              status: response.status,
              message: errorText,
            });
            onError?.(error, dispatch);
            return rejectWithValue(error);
          }

          return response;
        }

        // ✅ If result is a custom object (not a Response), treat as structured data
        if (typeof response === "object" && response !== null) {
          if (response.ok === false || response.status >= 400) {
            const error = buildStandardError({
              source,
              type: "api",
              status: response.status,
              message: response.message || "Unknown API error",
            });
            onError?.(error, dispatch);
            return rejectWithValue(error);
          }

          return response;
        }

        // ❌ Unexpected result type
        const error = buildStandardError({
          source,
          type: "unknown",
          message: "Invalid API response format",
        });
        onError?.(error, dispatch);
        return rejectWithValue(error);
      } catch (err) {
        const error = buildStandardError({
          source,
          type: "unknown",
          message: err.message,
          detail: err.stack,
        });
        onError?.(error, dispatch);
        return rejectWithValue(error);
      }
    }
  );
};


